import { IRenderingCommonContext } from "@commonRendering/types";
import { BackgroundData, IBackgroundStyle, IBurgerIconStyle, IMobileHeaderSectionData, IMobileHeaderSectionMenuData, MobileHeaderSectionData } from "@pageBuilder/typings";
import { AlphaChannelService } from "src/services/alpha-channel.service";
import { CssProperties } from "../../../models/css-property";
import { RendererBase } from "../../../shared/renderer-base";
import { Base64Util } from "../../../utils/base64-util";

export class HeaderMobileSectionRenderer extends RendererBase<IMobileHeaderSectionData> {

    private readonly DATA_ATTRIBUTE = "data-mobile-header-links";
    private readonly alphaChannelService: AlphaChannelService;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.alphaChannelService = new AlphaChannelService();
    }

    protected override createNewInternal(): HTMLElement {
        const element = document.createElement("section");
        element.setAttribute("data-section", "headerMobileSection");
        element.id = "mobile-header-section";

        const content = this.createContentElement();

        const background = document.createElement("div");
        background.classList.add("background");

        element.append(background, content);

        return element;
    }

    protected override renderInternal(element: HTMLElement, data: IMobileHeaderSectionData): HTMLElement {
        this.cleanupAllElementStyles(element);
        this.renderStyles(element, data);
        this.setAttribute(element, data);
        this.disableTabIndex(element);

        return element;
    }

    private renderStyles(element: HTMLElement, data: IMobileHeaderSectionData): void {
        if (data?.headerBackground) {
            this.renderMobileHeaderBackgroundStyle(element, data.headerBackground);
        }

        if (data?.burgerIcon) {
            this.renderBurgerIconStyle(element, data.burgerIcon);
        }

        if (data.logo.logoMobileHeight) {
            this.renderLogoStyle(element, data.logo.logoMobileHeight);
        }

        this.renderItemsStyle(element, data);

    }

    public renderItemsStyle(element: HTMLElement, data: IMobileHeaderSectionData): void {
        const props = new CssProperties();
        const backgroundProps = new CssProperties();

        props.tryAdd("--boxes-color", data.navigation.style.boxesColor);

        if (data.navigation.style.color) {
            props.tryAdd("--items-icon-color", this.alphaChannelService.change(data.navigation.style.color, 0.5));
            props.tryAdd("--items-icon-background-color", this.alphaChannelService.change(data.navigation.style.color, 0.08));
            props.tryAdd("--items-username-color", data.navigation.style.color);
            props.tryAdd("--items-email-color", this.alphaChannelService.change(data.navigation.style.color, 0.5));
            props.tryAdd("--items-dividers-color", this.alphaChannelService.change(data.navigation.style.color, 0.5));
            props.tryAdd("--items-link-color", data.navigation.style.color);
        }

        backgroundProps.tryAdd("--navigation-area-background-color", data.background.color ?? MobileHeaderSectionData.defaultData.background.color);

        this.styleService.setStyle(element, `#${element.id} .nav-wrapper`, props.data);
        this.styleService.setStyle(element, `#${element.id} .mobile-menu-background`, backgroundProps.data);
    }

    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    protected createContentElement(): HTMLElement {
        const content = document.createElement("div");
        content.classList.add("content");
        return content;
    }

    public override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        const selectors: string[] = [`#${element.id}`];
        const navigationSelectors = [`#${element.id} .nav-wrapper`, `#${element.id} .mobile-menu-background`];
        const logoSelectors = [`#${element.id} .logo-wrapper .logo`];
        const burgerIconSelector = `#${element.id} .burger-wrapper`;
        const mobileHeaderBackgroundStyle = `#${element.id} .background`;
        selectors.push(burgerIconSelector, mobileHeaderBackgroundStyle, ...navigationSelectors, ...logoSelectors);
        return selectors;
    }

    private renderMobileHeaderBackgroundStyle(element: HTMLElement, background: IBackgroundStyle): void {
        const props = new CssProperties();
        props.tryAdd("--mobile-header-background-color", `${background?.color ?? BackgroundData.defaultHeaderData.style?.color}`);

        this.styleService.setStyle(element, `#${element.id} .background`, props.data);
    }

    private renderBurgerIconStyle(header: HTMLElement, burgerIcon: IBurgerIconStyle): void {
        const props = new CssProperties();
        props.tryAdd("--burger-icon-color", burgerIcon.color);

        this.styleService.setStyle(header, `#${header.id} .burger-wrapper`, props.data);
    }

    public renderLogoStyle(element: HTMLElement, logoMobileHeight: number): void {
        const cssItems = new CssProperties();
        cssItems.tryAdd("height", `${logoMobileHeight}px`);

        this.styleService.setStyle(element, `#${element.id} .logo-wrapper .logo`, cssItems.data);
    }

    private setAttribute(element: HTMLElement, data: IMobileHeaderSectionData): void {
        const mobileHeaderSectionMenuData = this.getMobileHeaderSectionMenuData(data);
        const json = Base64Util.serialize(mobileHeaderSectionMenuData) || "";

        element.setAttribute(this.DATA_ATTRIBUTE, json);
    }

    private getMobileHeaderSectionMenuData(data: IMobileHeaderSectionData): IMobileHeaderSectionMenuData {
        return {
            mobileLayoutType: data.mobileLayoutType,
            logo: data.logo,
            links: data.navigation.links,
            navigationPagesInfo: data.navigationPagesInfo
        };
    }

    private disableTabIndex(element: HTMLElement): void {
        if (!this.isEditorRendering) {
            return;
        }

        element.querySelectorAll<HTMLElement>(".nav-item-name").forEach(item => {
            const link = item.querySelector<HTMLAnchorElement>("a");
            if (link) {
                link.tabIndex = -1;
            }
        });
    }

}
